"use client"
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { RightDrawer } from '@/shared/ui/RightDrawer'
import { Avatar } from '@/shared/ui/Avatar'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { signOut as signOutApi } from '@/modules/auth/services/authService'
import { getLogo } from '@/modules/branding/brandingService'
import { isLightTheme } from '@/lib/theme'
import { cn } from '@/lib/utils'

export type DrawerItem = { label: string; to?: string; onClick?: () => void }

type UserDrawerMenuProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  items: DrawerItem[]
  className?: string
}

export function UserDrawerMenu({ open, onOpenChange, items, className }: UserDrawerMenuProps) {
  const { user, profile } = useAuth()
  const nav = useNavigate()
  const [logoUrl, setLogoUrl] = React.useState<string | null>(null)

  React.useEffect(() => { (async () => setLogoUrl(await getLogo('home', isLightTheme())))() }, [])

  function handleItem(it: DrawerItem) {
    return () => {
      onOpenChange(false)
      if (it.onClick) { it.onClick(); return }
      if (it.to) { nav(it.to) }
    }
  }

  return (
    <RightDrawer open={open} onOpenChange={onOpenChange} className={cn('lg:hidden', className)} header={logoUrl ? <img src={logoUrl} alt="logo" className="h-[1.6rem] w-auto" /> : ' '} >
      <div className="flex flex-col items-center pb-4">
        <Avatar src={profile?.avatar_url || ''} alt={profile?.full_name || user?.email || ''} className="h-20 w-20" />
        <div className="mt-2 min-w-0 text-center">
          <div className="truncate text-sm font-medium">{profile?.full_name || 'Usuário'}</div>
          {user?.email && <div className="truncate text-xs text-foreground/70">{user.email}</div>}
        </div>
      </div>

      <div className="space-y-2">
        {items.map((it, idx) => (
          <button
            key={idx}
            type="button"
            className="w-full rounded-md border border-border bg-muted/30 px-3 py-2 text-left text-sm hover:bg-muted"
            onClick={handleItem(it)}
          >
            {it.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        <SignOutButton onAfter={() => { onOpenChange(false); nav('/') }} />
      </div>
    </RightDrawer>
  )
}

function SignOutButton({ onAfter }: { onAfter?: () => void }) {
  const [loading, setLoading] = React.useState(false)
  async function onClick() {
    if (loading) return
    setLoading(true)
    try { await signOutApi() } finally { setLoading(false); onAfter?.() }
  }
  return (
    <button type="button" onClick={onClick} className="w-full rounded-md border border-border bg-destructive/10 px-3 py-2 text-center text-sm text-destructive-foreground hover:bg-destructive/20">
      {loading ? 'Saindo...' : 'Sair'}
    </button>
  )
}

